import {
  Component,
  computed,
  effect,
  inject,
  input,
  OnInit,
  viewChild,
  signal,
  Signal,
  WritableSignal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatToolbarModule } from '@angular/material/toolbar';

import { map, groupBy, orderBy, filter } from 'lodash-es';
import { Project, Participant, Assessment } from '../models';
import { ApiService } from '../api.service';
import { ProjectDomainService } from '../domain-services/project-domain.service';
import { ParticipantDomainService } from '../domain-services/participant-domain.service';
import { ACTIVE_CLIENT } from '../../main';
import { finalize, catchError, of, filter as rxFilter, map as rxMap } from 'rxjs';
import { AssessmentDomainService } from '../domain-services/assessment-domain.service';
import { ReportDomainService } from '../domain-services/report-domain.service';
import { UserPermissionsService } from '../user-permissions.service';
import { PackageDomainService } from '../domain-services/package-domain.service';

@Component({
  selector: 'app-participant-list',
  standalone: true,
  templateUrl: './participant-list.component.html',
  styleUrls: ['./participant-list.component.scss'],
  imports: [
    CommonModule,
    RouterModule,
    MatProgressSpinnerModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatToolbarModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ParticipantListComponent {
  paginator = viewChild(MatPaginator);
  sort = viewChild(MatSort);

  project = input<Project>();
  participants = input<Participant[]>();
  assessments = input<Assessment[]>();

  private readonly projectDomain = inject(ProjectDomainService);
  private readonly participantDomain = inject(ParticipantDomainService);
  private readonly assessmentDomain = inject(AssessmentDomainService);
  readonly permissions = inject(UserPermissionsService);
  private readonly reportDomain = inject(ReportDomainService);
  private readonly packageDomain = inject(PackageDomainService);

  projectView = computed<Project | null>(
    () => this.projectDomain.currentProject() ?? this.project() ?? null,
  );

  // Prefer domain-backed participants so inline edits propagate
  // back into the table without requiring a full resolver reload.
  participantsForDisplay = computed<Participant[]>(() => {
    return this.participantDomain.projectParticipants() ?? this.participants() ?? [];
  });

  assessmentsForDisplay = computed<Assessment[]>(() => {
    return this.assessmentDomain.projectAssessments() ?? this.assessments() ?? [];
  });

  viewData = computed(() => {
    const groupedAssessments = groupBy(this.assessmentsForDisplay(), 'participantId');
    const rows = map(this.participantsForDisplay(), (participant) => {
      const x = groupedAssessments[participant.id] ?? [];

      return {
        ...participant,
        total: x.length,
        completed: filter(x, (a) => a.completedDate).length,
        latest: orderBy(x, ['completedDate'], ['desc'])[0]?.completedDate ?? null,
        assessments: x,
      };
    });

    return new MatTableDataSource<ViewData>(rows);
  });

  // Matches the project-detail rule: no assigned reports or packages means
  // no invite entry point. The route resolves both collections for this
  // project, so these signals always reflect the project being viewed.
  hasAssignedReports = computed<boolean>(() => {
    const reports = this.reportDomain.projectReports() ?? [];
    const packages = this.packageDomain.projectPackages() ?? [];
    return reports.length > 0 || packages.length > 0;
  });

  // We can safely say there are no ready reports
  // when no participant has any completed assessments.
  hasAnyPotentialReports = computed(() => {
    const data = this.viewData().data as ViewData[];
    return data.some((row) => row.completed > 0);
  });

  constructor() {
    const projectId = this.route.snapshot.paramMap.get('projectId');
    if (projectId) {
      if (!this.projectDomain.currentProject()) {
        this.projectDomain
          .loadProject(projectId)
          .pipe(catchError(() => of(null)))
          .subscribe();
      }

      if (!this.participantDomain.projectParticipants()) {
        this.participantDomain
          .loadProjectParticipants(projectId)
          .pipe(catchError(() => of([])))
          .subscribe();
      }

      if (!this.assessmentDomain.projectAssessments()) {
        this.assessmentDomain
          .loadProjectAssessments(projectId)
          .pipe(catchError(() => of([])))
          .subscribe();
      }
    }

    effect(() => {
      const p = this.paginator();
      if (p) this.viewData().paginator = p;
      const s = this.sort();
      if (s) {
        this.viewData().sort = s;
        this.viewData().sortingDataAccessor = (item: any, property: string) => {
          if (property === 'completionDates') {
            if (item.latest) {
              return Date.parse(item.latest);
            }
            return item.completed;
          }
          const value = item[property];
          return typeof value === 'string' ? value.toLowerCase() : value;
        };
        this.viewData().sort?.sort({
          id: 'completionDates',
          start: 'asc',
          disableClear: false,
        });
      }
    });
  }

  selected: WritableSignal<{ participant: Participant; assessments: Assessment[] | null } | null> =
    signal(null);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  // True while a :participantId child route is active. On phones the list and
  // the detail are alternating views rather than two columns, so this drives
  // which one is on screen; from lg up both are visible and it only gates the
  // mobile-only back control.
  hasSelection = toSignal(
    this.router.events.pipe(
      rxFilter((e) => e instanceof NavigationEnd),
      rxMap(() => !!this.route.firstChild),
    ),
    { initialValue: !!this.route.snapshot.firstChild },
  );

  // Returns to the list view on phones by deactivating the child route.
  closeParticipant(): void {
    const projectId = this.projectView()?.id ?? this.route.snapshot.paramMap.get('projectId');
    this.selected.set(null);
    if (projectId) {
      this.router.navigate(['/projects', projectId, 'participants'], {
        state: { showLoading: false },
      });
    }
  }

  selectParticipant(row: ViewData) {
    this.selected.set({ participant: row as Participant, assessments: row.assessments });
    this.router.navigate([row.id], {
      relativeTo: this.route,
      state: { showLoading: false },
    });
  }

  /* Download Project Reports */
  private apiService = inject(ApiService);
  private activeClient = inject(ACTIVE_CLIENT);
  error = signal<string | null>(null);
  downloading = signal(false);
  download() {
    this.error.set(null);
    this.downloading.set(true);

    const project = this.projectView();
    if (!project?.id) {
      this.error.set('Missing project id.');
      this.downloading.set(false);
      return;
    }

    // Project-level downloads are always zip archives, named '{project name}.zip'.
    const downloadName = `${this.sanitizeFilename(project.name ?? 'reports')}.zip`;

    this.apiService
      .downloadReports({ clientId: this.activeClient, projectId: project.id })
      .pipe(finalize(() => this.downloading.set(false)))
      .subscribe({
        next: ({ url, revoke }) => {
          const a = document.createElement('a');
          a.href = url;
          a.download = downloadName;
          a.click();
          // revoke after the browser processes the click
          requestAnimationFrame(revoke);
        },
        error: (err) => this.error.set('Failed to download.'),
      });
  }

  private sanitizeFilename(value: string): string {
    // Replace characters not permitted in filenames across platforms.
    return value.replace(/[\\/:*?"<>|]/g, '_').trim();
  }
}

export interface ViewData extends Participant {
  total: number;
  completed: number;
  latest: string | null;
  assessments: Assessment[] | null;
}
